import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics/trackEvents';
import { trackAnonymousPageView } from '@/lib/analytics/anonymousTracking';
import { useAuth } from '@/contexts/AuthContext';
import { useAnalyticsConsent } from './useAnalyticsConsent';

/**
 * Global page tracking hook with two-tier GDPR compliance.
 * 
 * Tier 1 (No Consent): Anonymous aggregate tracking
 * - Only page path, date, referrer category
 * - No localStorage access, no session_id
 * - Legal basis: Legitimate interest (Art. 6(1)(f) GDPR)
 * 
 * Tier 2 (With Consent): Full analytics tracking
 * - Session tracking, device info, user ID
 * - Legal basis: Consent (Art. 6(1)(a) GDPR)
 *
 * GDPR Art. 21 compliance:
 * Authenticated users who set `object_to_analytics = true` short-circuit BOTH
 * tiers — no `usage_statistics` row is written. The objection overrides the
 * legitimate-interest fallback (mem://compliance/gdpr-objection-enforcement).
 */
export const usePageTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { canTrack, loading, userObjectsToAnalytics } = useAnalyticsConsent();
  const hasLoggedStatus = useRef<string | null>(null);
  
  useEffect(() => {
    // Wait for consent check to complete
    if (loading) return;
    
    const currentPath = location.pathname + location.search;
    
    // Exclude admin, dev, and paper detail pages from tracking
    // Paper detail pages have their own specific tracking with metadata
    const excludedPrefixes = ['/admin', '/dev', '/paper'];
    const shouldExclude = excludedPrefixes.some(prefix => 
      location.pathname.startsWith(prefix)
    );
    
    if (shouldExclude) {
      if (import.meta.env.DEV) {
        console.log(`Page NOT tracked (admin/dev): ${currentPath}`);
      }
      return;
    }

    // GDPR Art. 21 — honor active objection across BOTH tracking tiers.
    if (userObjectsToAnalytics) {
      if (import.meta.env.DEV && hasLoggedStatus.current !== 'objected') {
        console.log('[Objection] Analytics suppressed — object_to_analytics is true');
        hasLoggedStatus.current = 'objected';
      }
      return;
    }
    
    if (canTrack) {
      // Tier 2: Full analytics with consent
      if (import.meta.env.DEV && hasLoggedStatus.current !== 'full') {
        console.log(`[Tier 2] Full analytics enabled`);
        hasLoggedStatus.current = 'full';
      }
      trackPageView(currentPath, user?.id);
    } else {
      // Tier 1: Anonymous aggregate tracking (consent-free, legitimate interest)
      if (import.meta.env.DEV && hasLoggedStatus.current !== 'anonymous') {
        console.log(`[Tier 1] Anonymous aggregate tracking (no consent)`);
        hasLoggedStatus.current = 'anonymous';
      }
      trackAnonymousPageView(currentPath);
    }
  }, [location.pathname, location.search, user?.id, canTrack, loading, userObjectsToAnalytics]);
};
